import { formatCosts, formatDollarsPerMw } from "in_queue/utils/formatting";
import { formatRank } from "in_queue/utils/rankFormatting";
import { UpgradeContributionRow } from "in_queue/utils/upgradeContributions";
import { useMemo, useState } from "react";
import css from "./charts.module.scss";

type BreakdownSortKey = "project" | "rank" | "perMw" | "cost" | "pct";
type BreakdownSortDir = "asc" | "desc";

export interface ProjectRankStats {
    rank: number;
    total: number;
    percentile: number;
    costPerMw: number | undefined;
}

interface Props {
    upgrade: UpgradeContributionRow;
    currentProjectId: string;
    /** Per-project rank stats sourced from the parent chart's ranking pool. */
    projectStats: Map<string, ProjectRankStats>;
    onClose: () => void;
}

/**
 * Inline breakdown for a single upgrade — opened when a user clicks a
 * row in the SharedUpgradesChart. Columns are sortable, default sort is
 * by % of upgrade desc (most-allocated first).
 */
export const UpgradeBreakdownTable: React.FC<Props> = ({
    upgrade,
    currentProjectId,
    projectStats,
    onClose
}) => {
    const [sortKey, setSortKey] = useState<BreakdownSortKey>("pct");
    const [sortDir, setSortDir] = useState<BreakdownSortDir>("desc");

    const onHeaderClick = (key: BreakdownSortKey) => {
        if (key === sortKey) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            // Sensible default direction per column.
            setSortDir(key === "project" || key === "rank" ? "asc" : "desc");
        }
    };

    const sorted = useMemo(() => {
        const rows = upgrade.contributions.slice();
        const dir = sortDir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            const sa = projectStats.get(a.projectId);
            const sb = projectStats.get(b.projectId);
            // Returns `undefined` for missing rank/$ MW values so they
            // can be sorted to the END regardless of direction (an
            // `Infinity` sentinel multiplied by dir would flip on
            // descending sorts and pull blanks to the top).
            const getVal = (
                c: { projectId: string; cost: number; pct: number },
                s: typeof sa
            ): number | string | undefined => {
                switch (sortKey) {
                    case "project":
                        return c.projectId;
                    case "rank":
                        return s?.percentile;
                    case "perMw":
                        return s?.costPerMw;
                    case "cost":
                        return c.cost;
                    case "pct":
                    default:
                        return c.pct;
                }
            };
            const va = getVal(a, sa);
            const vb = getVal(b, sb);
            // Always sink missing values to the bottom, regardless of dir.
            if (va === undefined && vb === undefined) return 0;
            if (va === undefined) return 1;
            if (vb === undefined) return -1;
            if (typeof va === "string" && typeof vb === "string") {
                return va.localeCompare(vb) * dir;
            }
            return ((va as number) - (vb as number)) * dir;
        });
        return rows;
    }, [upgrade.contributions, projectStats, sortKey, sortDir]);

    const headerCell = (key: BreakdownSortKey, label: string) => (
        <th
            className={css.breakdownHeaderCell}
            onClick={() => onHeaderClick(key)}
            role="button"
            aria-sort={
                sortKey === key
                    ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                    : "none"
            }
        >
            {label}
            {sortKey === key && (
                <span className={css.sortIndicator}>
                    {sortDir === "asc" ? " ▲" : " ▼"}
                </span>
            )}
        </th>
    );

    return (
        <div className={css.breakdownPanel}>
            <div className={css.breakdownHeader}>
                <div>
                    <b>{upgrade.name}</b>
                    <span className={css.tooltipSubtle}>
                        {" "}
                        · Total {formatCosts(upgrade.totalCost)} ·{" "}
                        {upgrade.contributions.length} projects allocated
                    </span>
                </div>
                <button
                    className={css.closeButton}
                    onClick={onClose}
                    aria-label="Close breakdown"
                >
                    ×
                </button>
            </div>
            <table className={css.breakdownTable}>
                <thead>
                    <tr>
                        {headerCell("project", "Project")}
                        {headerCell("rank", "Rank ($/MW)")}
                        {headerCell("perMw", "$/MW")}
                        {headerCell("cost", "Allocated $")}
                        {headerCell("pct", "% of upgrade")}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((c) => {
                        const stats = projectStats.get(c.projectId);
                        const isYou = c.projectId === currentProjectId;
                        return (
                            <tr
                                key={c.projectId}
                                className={isYou ? css.breakdownYou : ""}
                            >
                                <td>
                                    {c.projectId}
                                    {isYou && (
                                        <span className={css.tooltipSubtle}>
                                            {" "}
                                            (this project)
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {stats
                                        ? formatRank(
                                              stats.rank,
                                              stats.total,
                                              stats.percentile
                                          )
                                        : "—"}
                                </td>
                                <td>
                                    {stats?.costPerMw !== undefined
                                        ? formatDollarsPerMw(stats.costPerMw)
                                        : "—"}
                                </td>
                                <td>{formatCosts(c.cost)}</td>
                                <td>{c.pct.toFixed(1)}%</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
